import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { LocationRouteParams } from 'pages/routes';

export const useLocalHistory = () => {
    const navigate = useNavigate();
    const { state: routeState } = useLocation();
    const { merchantId, locationId, scenario } = useParams<LocationRouteParams>();
    const getParsedPath = React.useCallback(
        (path: string, overrideParams: Record<string, string> = {}) => {
            const params: Record<string, string | undefined> = {
                merchantId,
                locationId,
                scenario,
                ...overrideParams
            };
            return Object.keys(params).reduce(
                (acc, key) => acc.replace(new RegExp(`:${key}`, 'g'), params[key] ?? `:${key}`),
                path
            );
        },
        [merchantId, locationId, scenario]
    );

    const replace = React.useCallback(
        (
            path: string,
            params: Record<string, string> = {},
            searchState = '',
            state: Record<string, any> = {}
        ) => {
            const parsedPath = getParsedPath(path, params);
            navigate(
                {
                    pathname: parsedPath,
                    search: searchState
                },
                {
                    replace: true,
                    state: {
                        ...state,
                        ...routeState,
                        search: new URLSearchParams(searchState).toString(),
                        pathname: parsedPath
                    }
                }
            );
        },
        [navigate, getParsedPath, routeState]
    );
    const push = React.useCallback(
        (
            path: string,
            params: Record<string, string> = {},
            searchState = '',
            state: Record<string, any> = {}
        ) => {
            const parsedPath = getParsedPath(path, params);
            navigate(
                {
                    pathname: parsedPath,
                    search: searchState
                },
                {
                    state: {
                        ...state,
                        ...routeState,
                        search: new URLSearchParams(searchState).toString(),
                        pathname: parsedPath
                    }
                }
            );
        },
        [navigate, getParsedPath, routeState]
    );
    const goBack = React.useCallback(() => navigate(-1), [navigate]);
    return { push, navigate, goBack, getParsedPath, replace };
};
